import type { Element, Locale } from '../constants'

/**
 * **이름 풀이 한자 표 — Unihan 에서 만든 것** (승인).
 *
 * 손으로 적은 251자 표로는 「歐陽娜娜」 의 歐·娜, 간체 「吕伟」, 일본 신자체 「辺」 을 못 읽었다. 공개 자료
 * Unicode Unihan(18.0.0, Unicode License v3)에서 `scripts/hanja-build.mjs` 로 **만들 때 한 번** 뽑은 표를 쓴다 —
 * 앱이 돌 때 바깥에 묻지 않는다. 표가 커서(약 200KB) **이름 풀이 화면에서만** 불러온다 (`loadHanjaTable`).
 *
 * 읽는 갈래는 화면 언어로 정한다: 한국어 = 한국 한자음 · 중국어(간체·번체) = 병음 · 일본어 = 음독(없으면 훈독) ·
 * 그 밖(라틴 글자 언어) = 병음. 이 표는 **그 전 방법(서버가 준 음·251자 표)으로 못 읽은 글자에만** 쓴다 —
 * 그래서 전에도 다 읽히던 이름은 기운이 그대로이고 저장 캐시 열쇠도 그대로다.
 */
type System = 'ko' | 'zh' | 'ja'
const ORDER: Element[] = ['wood', 'fire', 'earth', 'metal', 'water']

let table: Record<System, Map<string, number>> | null = null
let loading: Promise<void> | null = null

function parse(s: string): Map<string, number> {
  const m = new Map<string, number>()
  const cps = [...s]
  for (let i = 0; i + 1 < cps.length; i += 2) m.set(cps[i]!, Number(cps[i + 1]))
  return m
}

/** 표를 받는다 (한 번만). 이름 풀이 화면이 처음 열릴 때 부른다. */
export function loadHanjaTable(): Promise<void> {
  if (table) return Promise.resolve()
  loading ??= import('../../data/hanjaElements.json').then((mod) => {
    const doc = (mod as { default?: { ko: string; zh: string; ja: string } }).default ?? (mod as unknown as { ko: string; zh: string; ja: string })
    table = { ko: parse(doc.ko), zh: parse(doc.zh), ja: parse(doc.ja) }
  })
  return loading
}

export function hanjaTableReady(): boolean {
  return table !== null
}

/** 화면 언어 → 읽는 갈래 */
export function hanjaSystem(locale: Locale | undefined): System {
  if (!locale || locale === 'ko') return 'ko'
  if (locale === 'ja') return 'ja'
  return 'zh'
}

/**
 * 그 글자의 첫소리 기운. 표가 아직 없거나 표에 없으면 null.
 * `first` 는 성씨 자리 — 한국 한자음의 두음법칙(ㄹ·ㄴ + ㅣ계열 → ㅇ)을 건다.
 */
export function hanjaTableElement(ch: string, system: System, first: boolean): Element | null {
  const code = table?.[system].get(ch)
  if (code === undefined) return null
  if (code === 5) return first ? 'earth' : 'fire'
  if (code === 6) return 'fire'
  return ORDER[code] ?? null
}
